import { useEffect } from 'react';

import { isShiftJourney, outcomeOptions, scriptParts } from './overviewModel';
import {
  C,
  DesignSelect,
  FilledButton,
  FONT,
  OutlineButton,
  Plain,
  text14,
} from './ui';
import { resultNote } from '../CallTasksTab/callTaskFormat';
import { DAYS, parseRoles } from '../CallTasksTab/callTaskForm';
import useCallTaskDialog from '../CallTasksTab/useCallTaskDialog';
import { outcomeLabel } from '../shared/labels';

import useAuth from '@/hooks/useAuth';
import styled from '@/styles';
import { CallTask, CallTaskOutcome } from '@/services/lifecycleAPI';

// The design's skill list. Roles already saved on the task are kept as
// options too, so nothing on the task disappears from the list.
const SKILLS = [
  'Server',
  'Bartender',
  'Barback',
  'Dishwasher',
  'Line cook',
  'Prep cook',
  'Event staff',
  'Warehouse',
];

const Notes = styled('textarea', {
  height: 100,
  borderRadius: 12,
  border: 'none',
  boxShadow: `inset 0 0 0 1px ${C.stroke}`,
  padding: 12,
  boxSizing: 'border-box',
  fontFamily: FONT,
  fontSize: 14,
  lineHeight: 1.4,
  color: C.black,
  resize: 'none',
  outline: 'none',
  '&:focus': { boxShadow: `inset 0 0 0 1.5px ${C.green}` },
});

const numericUserId = (id: unknown): number | null => {
  const value = Number(id);
  return Number.isInteger(value) && value > 0 ? value : null;
};

type Props = {
  task: CallTask;
  journeyKey: string;
  onBack: () => void;
  onLogged: (task: CallTask, outcome: CallTaskOutcome) => void;
  onChanged: () => void;
  onDirty: (dirty: boolean) => void;
};

const label = { ...text14, fontWeight: 600, color: C.black } as const;

const LogCallView = ({
  task,
  journeyKey,
  onBack,
  onLogged,
  onChanged,
  onDirty,
}: Props) => {
  const { currentAdmin } = useAuth();
  const myUserId = numericUserId(currentAdmin?.user?.id);
  const dialog = useCallTaskDialog({
    taskId: task.id,
    onChanged,
    onCompleted: () => onLogged(task, dialog.form.outcome as CallTaskOutcome),
    surface: 'step_modal',
  });
  const { form, setField } = dialog;
  // A task someone else completed or the engine closed (seen after a 409 and
  // Reload) cannot be logged again: Log call would only repeat the 409, and its
  // saved outcome is not an edit to discard (review D-03).
  const closed = Boolean(dialog.task && dialog.task.status !== 'open');
  const dirty = !closed && (dialog.hasChanges || Boolean(form.outcome));
  useEffect(() => {
    onDirty(dirty);
  }, [dirty, onDirty]);
  useEffect(() => () => onDirty(false), [onDirty]);
  const shift = isShiftJourney(journeyKey);
  const script = dialog.task?.script ?? '';
  const parts = scriptParts(script);

  const assigneeOptions = dialog.assignees.map((person) => ({
    value: String(person.user_id),
    label: person.user_id === myUserId ? `${person.name} (you)` : person.name,
  }));
  const assignedId = dialog.task?.assigned_to_user_id;
  if (
    assignedId !== null &&
    assignedId !== undefined &&
    !assigneeOptions.some((o) => o.value === String(assignedId))
  ) {
    assigneeOptions.unshift({
      value: String(assignedId),
      label: dialog.task?.assigned_to_name ?? `User ${assignedId}`,
    });
  }

  const roles = parseRoles(form.roles);
  const skillOptions = [
    ...SKILLS,
    ...roles.filter((role) => !SKILLS.includes(role)),
  ].map((skill) => ({ value: skill, label: skill }));

  const ready =
    !closed &&
    Boolean(form.outcome) &&
    !dialog.busy &&
    !dialog.formError &&
    Boolean(dialog.task);

  return (
    <div data-testid="logCallView">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          padding: '0 24px 16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={label}>Script</span>
          <div
            data-testid="logCallScript"
            style={{
              borderRadius: 12,
              background: C.scriptBg,
              boxShadow: `inset 0 0 0 1px ${C.stroke}`,
              padding: 12,
              ...text14,
              fontWeight: 700,
              color: C.black,
            }}
          >
            {dialog.load === 'loading'
              ? 'Loading the script...'
              : dialog.load === 'ready'
                ? parts.map((part, index) => (
                    <span key={index}>
                      {index > 0 && <b>[YOUR NAME]</b>}
                      {part}
                    </span>
                  ))
                : (dialog.loadError ?? 'Could not load the call task.')}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 20 }}>
            <DesignSelect
              label="Outcome"
              options={outcomeOptions(journeyKey).map((value) => ({
                value,
                label: outcomeLabel(value),
              }))}
              testId="logCallOutcome"
              value={form.outcome ? [form.outcome] : []}
              onChange={([value]) => setField('outcome', value ?? '')}
            />
            <DesignSelect
              label="Assigned to"
              options={assigneeOptions}
              testId="logCallAssignee"
              value={form.assignee ? [form.assignee] : []}
              onChange={([value]) => setField('assignee', value ?? '')}
            />
          </div>
          {dialog.assigneesError && (
            <span style={{ ...text14, color: C.red }}>
              {dialog.assigneesError}
            </span>
          )}
          {myUserId !== null && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Plain
                data-testid="logCallAssignMe"
                style={{ ...text14, fontWeight: 600, color: C.green }}
                type="button"
                onClick={() => setField('assignee', String(myUserId))}
              >
                Assign to me
              </Plain>
            </div>
          )}
        </div>
        {shift && (
          <>
            <DesignSelect
              multiple
              openUp
              label="Preferred skills"
              options={skillOptions}
              placeholder="Select skill/s"
              testId="logCallSkills"
              value={roles}
              onChange={(list) => setField('roles', list.join(', '))}
            />
            {dialog.formError && (
              <span
                role="alert"
                style={{ ...text14, color: C.red, fontWeight: 600 }}
              >
                {dialog.formError}
              </span>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={label}>Preferred days</span>
              <div
                role="group"
                aria-label="Preferred days"
                style={{ display: 'flex', gap: 16 }}
              >
                {DAYS.map((day) => {
                  const on = form.days.includes(day);
                  return (
                    <Plain
                      aria-pressed={on}
                      key={day}
                      style={{
                        flex: 1,
                        height: 36,
                        padding: '0 12px',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        background: on ? C.greenFill : C.white,
                        color: on ? C.white : C.black,
                        fontWeight: on ? 700 : 400,
                        boxShadow: `inset 0 0 0 1px ${on ? C.greenFill : C.stroke}`,
                      }}
                      type="button"
                      onClick={() =>
                        setField(
                          'days',
                          on
                            ? form.days.filter((d) => d !== day)
                            : [...form.days, day],
                        )
                      }
                    >
                      {day}
                    </Plain>
                  );
                })}
              </div>
            </div>
          </>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={label}>Notes</span>
          <Notes
            aria-label="Notes"
            data-testid="logCallNotes"
            placeholder="Add notes here..."
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
          />
        </div>
        {closed && dialog.task && (
          <div
            data-testid="logCallClosed"
            role="status"
            style={{ ...text14, color: C.black }}
          >
            {`This call task is closed: ${resultNote(dialog.task)}`}
          </div>
        )}
        {dialog.error && (
          <div
            data-testid="logCallError"
            role="alert"
            style={{
              ...text14,
              color: C.red,
              fontWeight: 600,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <span>{dialog.error}</span>
            {dialog.conflict && (
              <Plain
                style={{ color: C.green, fontWeight: 600 }}
                type="button"
                onClick={dialog.reload}
              >
                Reload
              </Plain>
            )}
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 16,
          padding: 16,
        }}
      >
        <OutlineButton type="button" onClick={onBack}>
          Go back
        </OutlineButton>
        <FilledButton
          data-testid="logCallSubmit"
          disabled={!ready}
          type="button"
          onClick={dialog.complete}
        >
          Log call
        </FilledButton>
      </div>
    </div>
  );
};

export default LogCallView;
